import React from 'react';
import cn from 'classnames';

import {
  type Datelike,
  formatDatelike,
  isBetweenDaysInclusive,
  isSameDay,
  preserveTime,
} from '../../services/DateTime';
import Calendar from '../Calendar';
import T from '../Translate';

import TimeSelect from './TimeSelect';

import styles from './datetimeInput.module.scss';

export enum DateTimeSide {
  DATE = 0,
  TIME = 1,
}

interface Props {
  selected: Datelike;
  range?: {
    from: Datelike;
    to: Datelike;
  };
  label: string;
  datePlaceholder?: string;
  timePlaceholder?: string;
  calendarTitle: string;
  dateOpened?: boolean;
  timeOpened?: boolean;
  min?: Datelike;
  max?: Datelike;
  disabledDates?: Datelike[];
  disabledWeekdays?: number[];
  enabledDates?: Datelike[];
  className?: string;
  showDate?: boolean;
  showTime?: boolean;

  onChange(datetime: Datelike, side: DateTimeSide): void | unknown;

  onDateClick(): void | unknown;

  onTimeClick(): void | unknown;
}

function DateTimeInput(props: Props, ref: React.MutableRefObject<HTMLDivElement>) {
  return (
    <div className={cn(styles.container, props.className)} ref={ref}>
      <div
        className={cn(styles.inputs, {
          'time-selectable': props.showDate,
          'date-opened': props.dateOpened,
          'date-filled': props.showDate,
          'time-opened': props.timeOpened,
          'time-filled': props.showTime,
        })}
      >
        <button className={styles.dateInput} onClick={props.onDateClick} type="button">
          <i className="icon icon-calendar" />
          <div className={styles.floatingLabel}>
            <div className={styles.dateButton}>
              {props.showDate ? formatDatelike(props.selected, 'd. MMMM') : <T id={props.datePlaceholder} />}
            </div>
            <T as="span" className={styles.label} id={props.label} noBreak />
          </div>
        </button>

        <button className={styles.timeInput} onClick={props.onTimeClick} type="button">
          <div className={styles.timeButton}>
            {props.showTime ? formatDatelike(props.selected, 'HH:mm') : <T id={props.timePlaceholder} />}
          </div>
        </button>
      </div>

      {props.dateOpened ? (
        <div className={styles.dateDropdown}>
          <Calendar
            date={props.selected}
            dateClassName={date => {
              const classes = ['button button-sm'];
              if (isSameDay(date, props.selected)) {
                classes.push('button-primary selected');
              }
              if (props.range && props.range.from && props.range.to) {
                if (isSameDay(date, props.range.from) || isSameDay(date, props.range.to)) {
                  classes.push('button-primary selected');
                }
                if (isBetweenDaysInclusive(date, props.range.from, props.range.to)) {
                  classes.push('in-range');
                }
              }
              return classes.join(' ');
            }}
            disabledDates={props.disabledDates}
            disabledWeekdays={props.disabledWeekdays}
            enabledDates={props.enabledDates}
            max={props.max}
            min={props.min}
            onDateClick={datetime => props.onChange(preserveTime(datetime, props.selected), DateTimeSide.DATE)}
            title={props.calendarTitle}
            weekdayClassName={() => 'button button-sm'}
          />
        </div>
      ) : null}
      {props.timeOpened ? (
        <div className={styles.timeDropdown}>
          <TimeSelect
            max={props.max}
            min={props.min}
            onChange={datetime => props.onChange(datetime, DateTimeSide.TIME)}
            time={props.selected}
          />
        </div>
      ) : null}
    </div>
  );
}

export default React.forwardRef(DateTimeInput);
